// scheduling/pages/CapacityReport.jsx
// Capacity report (4 / 8 / 13 / 26 weeks from any start week): dept → level → person drilldown.
// Shows scheduled/capacity per week with utilization bars and hover tooltips, plus each
// person's contract (FT / PT / Flex / Leave) so part-timers are visible at a glance.

import { useState, useEffect, useMemo } from 'react';
import { fetchStaff, fetchSchedule, fetchCalendars, fetchPTO, fetchHolidays } from '../services/schedData';
import { BRAND, STAFF_DEPT_GROUPS } from '../utils/schedConstants';
import { makeGetDayCap, makeGetContract, buildPtoByStaffDay, buildHolidayByDate, weekDayISOs, toISO as isoOf } from '../utils/capacity';
import { useUrlState } from '../../hooks/useUrlState';
import ContractChip from '../components/ContractChip';

// ── Helpers ─────────────────────────────────────────────────────────────────

function getMonday(d = new Date()) {
  const dt = new Date(d);
  const dow = dt.getDay();
  dt.setDate(dt.getDate() - (dow === 0 ? 6 : dow - 1));
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// Local-noon parse of 'YYYY-MM-DD' (never new Date('YYYY-MM-DD'), which UTC-parses and shifts a day).
function parseISO(iso) { return new Date(iso + 'T12:00:00'); }

function fmtShort(d) { return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }

function utilColor(pct) {
  if (pct >= 90) return { bg: '#FEF2F2', bar: '#EF4444', text: '#DC2626' };
  if (pct >= 70) return { bg: '#FFFBEB', bar: '#F59E0B', text: '#D97706' };
  return { bg: '#F0FDF4', bar: '#22C55E', text: '#15803D' };
}

// ── Level ordering for consistent grouping ──────────────────────────────────
const LEVEL_ORDER = ['Member', 'Principal', 'Senior Manager', 'Manager', 'Supervisor', 'Senior Accountant', 'Staff A', 'Staff B', 'Intern', 'Consultant'];
function levelSort(a, b) {
  const ai = LEVEL_ORDER.indexOf(a); const bi = LEVEL_ORDER.indexOf(b);
  return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
}

const WEEK_OPTIONS = [4, 8, 13, 26];
const COMPACT_FROM_WEEKS = 13;   // at 13+ weeks the cells drop to bar + "sch/cap" and the table scrolls sideways
const NAME_COL_W = 250;
const COMPACT_WEEK_W = 68;
const LEADERSHIP_LEVELS = new Set(['Member', 'Principal', 'Senior Manager']);
const isISODate = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(parseISO(v).getTime());
const officeOf = (s) => s.Office || s.OfficeCode || '';

// ── Component ───────────────────────────────────────────────────────────────

export default function CapacityReport() {
  const [staff, setStaff] = useState([]);
  const [entries, setEntries] = useState([]);
  // Capacity inputs. This page used to hardcode 40h/week for everyone, so it was simply
  // wrong for every part-timer, anyone on leave, and every PTO or holiday day.
  const [calendars, setCalendars] = useState([]);
  const [pto, setPto] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);        // staff / calendars / holidays (once)
  const [winLoading, setWinLoading] = useState(true);   // schedule + PTO (every window change)
  const [error, setError] = useState(null);
  // Filters + horizon live in the URL (?dept, ?hideLeadership, ?start, ?weeks, ?level, ?office,
  // ?contract) for shareable / navbot deeplinks. The name search is transient.
  const [deptFilter, setDeptFilter] = useUrlState('dept', 'all');
  const [hideLeadership, setHideLeadership] = useUrlState('hideLeadership', true); // default: hide Member, Principal, Sr Manager
  const [startParam, setStartParam] = useUrlState('start', '', { validate: (v) => v === '' || isISODate(v) }); // '' = this week
  const [weekCount, setWeekCount] = useUrlState('weeks', 8, { validate: WEEK_OPTIONS });
  const [levelFilter, setLevelFilter] = useUrlState('level', 'all');
  const [officeFilter, setOfficeFilter] = useUrlState('office', 'all');
  const [contractFilter, setContractFilter] = useUrlState('contract', 'all', { validate: ['all', 'ft', 'pt'] }); // pt = part-time, flex & leave
  const [search, setSearch] = useState('');
  const [startDraft, setStartDraft] = useState(null);   // date input text while typing a partial year
  const [expandedDepts, setExpandedDepts] = useState({});
  const [expandedLevels, setExpandedLevels] = useState({});
  const [tooltip, setTooltip] = useState(null);

  // Window: weekCount weeks from the Monday of the chosen start week (default: this week).
  // Keyed on the start string so baseMonday is a stable Date between renders.
  const startKey = startParam || toISO(getMonday());
  const baseMonday = useMemo(() => getMonday(parseISO(startKey)), [startKey]);
  const compact = weekCount >= COMPACT_FROM_WEEKS;
  const weeks = useMemo(() => Array.from({ length: weekCount }, (_, i) => {
    const mon = addDays(baseMonday, i * 7);
    const fri = addDays(mon, 4);
    return { idx: i, mon, fri, label: fmtShort(mon), end: fmtShort(fri), range: `${fmtShort(mon)}\u2013${fmtShort(fri)}` };
  }), [baseMonday, weekCount]);

  const endDate = useMemo(() => addDays(baseMonday, weekCount * 7 - 1), [baseMonday, weekCount]); // last Sunday
  const startISO = useMemo(() => toISO(baseMonday), [baseMonday]);
  const endISO = useMemo(() => toISO(endDate), [endDate]);

  // Static data: loaded once, not per window.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [staffData, calData, holData] = await Promise.all([
          fetchStaff(),
          fetchCalendars().catch(() => []),
          fetchHolidays().catch(() => []),
        ]);
        if (cancelled) return;
        setStaff((staffData || []).filter(s => s.IsActive));
        setCalendars(calData || []);
        setHolidays(holData || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Window data: re-fetched whenever the start week or length changes (deps are stable strings, not Date objects)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setWinLoading(true);
      try {
        const [schedData, ptoData] = await Promise.all([
          fetchSchedule({ startDate: startISO, endDate: endISO }),
          fetchPTO({ startDate: startISO, endDate: endISO }).catch(() => []),
        ]);
        if (cancelled) return;
        setEntries(schedData || []);
        setPto(ptoData || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setWinLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [startISO, endISO]);

  // Index entries by staffId → weekIdx → jobs
  const schedByStaffWeek = useMemo(() => {
    const map = {}; // staffId → { weekIdx → { scheduled, jobs: [{client, desc, hours}] } }
    for (const e of entries) {
      const sid = e.StaffId;
      if (!map[sid]) map[sid] = {};
      const d = new Date(typeof e.EntryDate === 'string' ? e.EntryDate.slice(0, 10) + 'T12:00:00' : e.EntryDate);
      const dayOffset = Math.floor((d - baseMonday) / (86400000));
      const wi = Math.floor(dayOffset / 7);
      if (wi < 0 || wi >= weekCount) continue;
      if (!map[sid][wi]) map[sid][wi] = { scheduled: 0, jobs: {} };
      map[sid][wi].scheduled += e.Hours || 0;
      // Group by job
      const jKey = `${e.ClientName}_${e.JobName}`;
      if (!map[sid][wi].jobs[jKey]) map[sid][wi].jobs[jKey] = { client: e.ClientName || '', desc: e.JobName || '', hours: 0 };
      map[sid][wi].jobs[jKey].hours += e.Hours || 0;
    }
    return map;
  }, [entries, baseMonday, weekCount]);

  // Contract per person, evaluated at the window's start date (same templates the capacity uses).
  const getContract = useMemo(() => makeGetContract({ staff, globalCalendars: calendars }), [staff, calendars]);

  // Staff after the dept + "Mgr & below" toggles. The level / office dropdowns list what is
  // present here, so they never offer a choice that would empty the report.
  const baseStaff = useMemo(() => {
    let list = deptFilter === 'all' ? staff : staff.filter(s => s.DepartmentCode === deptFilter);
    if (hideLeadership) list = list.filter(s => !LEADERSHIP_LEVELS.has(s.Level));
    return list;
  }, [staff, deptFilter, hideLeadership]);
  const levelOptions = useMemo(
    () => [...new Set(baseStaff.map(s => s.Level || 'Other'))].sort(levelSort),
    [baseStaff]);
  const officeOptions = useMemo(
    () => [...new Set(baseStaff.map(officeOf).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [baseStaff]);

  const searchTerm = search.trim().toLowerCase();

  // Build dept → level → person tree. Person filters apply here, so every group header and
  // total (all derived from the tree) reflects only the people who are visible.
  const tree = useMemo(() => {
    const filtered = baseStaff.filter(s => {
      if (levelFilter !== 'all' && (s.Level || 'Other') !== levelFilter) return false;
      if (officeFilter !== 'all' && officeOf(s) !== officeFilter) return false;
      if (contractFilter !== 'all') {
        const kind = getContract(s.StaffId, startISO)?.kind;
        if (contractFilter === 'ft' ? kind !== 'FT' : (!kind || kind === 'FT')) return false;
      }
      if (searchTerm && !(s.Name || '').toLowerCase().includes(searchTerm)) return false;
      return true;
    });
    const deptMap = {};
    for (const s of filtered) {
      const dCode = s.DepartmentCode || 'OTHER';
      if (!deptMap[dCode]) deptMap[dCode] = {};
      const level = s.Level || 'Other';
      if (!deptMap[dCode][level]) deptMap[dCode][level] = [];
      deptMap[dCode][level].push(s);
    }

    return STAFF_DEPT_GROUPS
      .filter(g => deptFilter === 'all' || g.id === deptFilter)
      .filter(g => deptMap[g.id])
      .map(g => ({
        ...g,
        levels: Object.entries(deptMap[g.id] || {})
          .sort(([a], [b]) => levelSort(a, b))
          .map(([levelName, people]) => ({
            id: `${g.id}_${levelName}`,
            name: levelName,
            people: people.sort((a, b) => (a.Name || '').localeCompare(b.Name || '')),
          })),
      }));
  }, [baseStaff, levelFilter, officeFilter, contractFilter, searchTerm, getContract, startISO, deptFilter]);

  // Real per-day capacity, via the same single source of truth the grid uses — so this
  // report, the grid, and the employee profile now agree for a given staffer and week.
  const getDayCap = useMemo(() => {
    const years = new Set();   // every year the window touches (a 26-week window can span two)
    for (let y = baseMonday.getFullYear(); y <= endDate.getFullYear(); y++) years.add(y);
    return makeGetDayCap({
      staff,
      globalCalendars: calendars,
      ptoByStaffDay: buildPtoByStaffDay(pto),
      holidayByDate: buildHolidayByDate(holidays, years),
    });
  }, [staff, calendars, pto, holidays, baseMonday, endDate]);

  // Monday ISO per week index, for summing a week's daily caps.
  const weekMondayISOs = useMemo(() => weeks.map(w => isoOf(w.mon)), [weeks]);

  // Aggregation helpers
  function personWeek(person, wi) {
    const data = schedByStaffWeek[person.StaffId]?.[wi];
    const scheduled = Math.round((data?.scheduled || 0) * 100) / 100;
    const jobs = data?.jobs ? Object.values(data.jobs).map(j => ({ ...j, hours: Math.round(j.hours * 100) / 100 })) : [];
    let capacity = 0;
    for (const iso of weekDayISOs(weekMondayISOs[wi])) capacity += getDayCap(person.StaffId, iso);
    return { scheduled, capacity: Math.round(capacity * 100) / 100, jobs };
  }

  function sumLevel(level, wi) {
    return level.people.reduce((a, p) => {
      const pw = personWeek(p, wi);
      return { cap: a.cap + pw.capacity, sch: a.sch + pw.scheduled };
    }, { cap: 0, sch: 0 });
  }

  function sumDept(dept, wi) {
    return dept.levels.reduce((a, l) => {
      const s = sumLevel(l, wi);
      return { cap: a.cap + s.cap, sch: a.sch + s.sch };
    }, { cap: 0, sch: 0 });
  }

  function grandTotal(wi) {
    return tree.reduce((a, d) => {
      const s = sumDept(d, wi);
      return { cap: a.cap + s.cap, sch: a.sch + s.sch };
    }, { cap: 0, sch: 0 });
  }

  // While searching by name, groups open on their own so the match is visible; a manual
  // toggle still wins. `cur` is the effective (rendered) state.
  const toggleDept = (id, cur) => setExpandedDepts(p => ({ ...p, [id]: !cur }));
  const toggleLevel = (id, cur) => setExpandedLevels(p => ({ ...p, [id]: !cur }));

  const onStartChange = (e) => {
    const v = e.target.value;
    setStartDraft(v);
    const y = Number(v.slice(0, 4));
    if (isISODate(v) && y >= 2000 && y <= 2100) {   // ignore partial years (0002, 0020, ...) typed on the way to 2027
      setStartParam(toISO(getMonday(parseISO(v))));
      setStartDraft(null);
    }
  };
  const resetStart = () => { setStartParam(''); setStartDraft(null); };

  // ── Render ────────────────────────────────────────────────────────────

  if (error) return <div className="sched-placeholder"><h2>Error</h2><p style={{ color: '#DC2626' }}>{error}</p></div>;

  const visibleStaff = tree.reduce((n, d) => n + d.levels.reduce((m, l) => m + l.people.length, 0), 0);
  const busy = loading || winLoading;

  return (
    <div className="sched-cap-root">
      {/* Header */}
      <div className="sched-cap-header">
        <div>
          <h2 className="sched-cap-title">Capacity Report</h2>
          <div className="sched-cap-subtitle">
            {weekCount}-Week Outlook &middot; {fmtShort(baseMonday)}, {baseMonday.getFullYear()} &ndash; {fmtShort(endDate)}, {endDate.getFullYear()} &middot; {visibleStaff}{visibleStaff !== staff.length ? ` of ${staff.length}` : ''} staff
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="sched-cap-toolbar">
        <span className="sched-cap-toolbar-label">DEPT</span>
        {[{ id: 'all', label: 'All' }, ...STAFF_DEPT_GROUPS].map(d => (
          <button key={d.id}
            className={`sched-mgr-pill${deptFilter === d.id ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setDeptFilter(d.id)}
          >{d.label?.split(' ')[0] || d.id}</button>
        ))}
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6B7280', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
          title="Hide Members, Principals, and Senior Managers">
          <input type="checkbox" checked={hideLeadership} onChange={e => setHideLeadership(e.target.checked)} />
          Mgr &amp; below
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, fontSize: 11, color: '#6B7280', alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 8, background: '#22C55E', borderRadius: 2 }} /> &lt;70%</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 8, background: '#F59E0B', borderRadius: 2 }} /> 70-90%</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 8, background: '#EF4444', borderRadius: 2 }} /> &gt;90%</span>
        </div>
      </div>

      {/* Horizon + person filters */}
      <div className="sched-cap-toolbar sched-cap-toolbar--sub">
        <label className="sched-cap-field">
          <span className="sched-cap-toolbar-label">START WEEK</span>
          <input type="date" className="sched-cap-input" value={startDraft ?? startISO} onChange={onStartChange}
            onBlur={() => setStartDraft(null)} title="Snaps to the Monday of the week you pick" />
        </label>
        <button className="sched-mgr-pill" onClick={resetStart} disabled={!startParam && startDraft == null}
          title="Jump back to the current week">This week</button>
        <label className="sched-cap-field">
          <span className="sched-cap-toolbar-label">WEEKS</span>
          <select className="sched-cap-input" value={weekCount} onChange={e => setWeekCount(Number(e.target.value))}>
            {WEEK_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <span className="sched-cap-toolbar-sep" />
        <label className="sched-cap-field">
          <span className="sched-cap-toolbar-label">LEVEL</span>
          <select className="sched-cap-input" value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
            <option value="all">All</option>
            {levelOptions.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <label className="sched-cap-field">
          <span className="sched-cap-toolbar-label">OFFICE</span>
          <select className="sched-cap-input" value={officeFilter} onChange={e => setOfficeFilter(e.target.value)}>
            <option value="all">All</option>
            {officeOptions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="sched-cap-field">
          <span className="sched-cap-toolbar-label">CONTRACT</span>
          <select className="sched-cap-input" value={contractFilter} onChange={e => setContractFilter(e.target.value)}
            title="Contract as of the start week">
            <option value="all">All</option>
            <option value="ft">Full-time only</option>
            <option value="pt">Part-time, flex &amp; leave</option>
          </select>
        </label>
        <input type="search" className="sched-cap-input sched-cap-search" placeholder="Search name…" value={search}
          onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Table */}
      <div className="sched-cap-table-wrap">
        {busy ? (
          <div className="sched-home-loading"><div className="sched-home-loading-text">Loading capacity data...</div></div>
        ) : (
        <table className={`sched-cap-table${compact ? ' sched-cap-table--compact' : ''}`}
          style={compact ? { width: NAME_COL_W + weekCount * COMPACT_WEEK_W } : undefined}>
          <thead>
            <tr>
              <th className="sched-cap-name-col">Dept / Level / Person</th>
              {weeks.map(w => (
                <th key={w.idx} className="sched-cap-week-th" title={w.range}>
                  <div style={{ fontWeight: 700 }}>{w.label}</div>
                  <div className="sched-cap-week-end" style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 400 }}>&ndash;{w.end}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>

            {/* Grand total row */}
            <tr className="sched-cap-grand">
              <td className="sched-cap-name-col sched-cap-grand-label">ALL DEPARTMENTS</td>
              {weeks.map((w, wi) => {
                const t = grandTotal(wi);
                const pct = t.cap > 0 ? (t.sch / t.cap) * 100 : 0;
                const barColor = pct >= 90 ? '#EF4444' : pct >= 70 ? '#FBBF24' : '#6CC24A';
                return (
                  <td key={wi} className="sched-cap-grand-cell" title={compact ? `${w.range}: ${Math.round(t.sch)}/${t.cap}h` : undefined}>
                    <div className="sched-cap-bar-track" style={{ background: 'rgba(255,255,255,0.15)' }}>
                      <div style={{ height: 4, width: `${Math.min(pct, 100)}%`, background: barColor, borderRadius: 2 }} />
                    </div>
                    <div className="sched-cap-cell-main" style={{ color: '#fff' }}>{Math.round(t.sch)}/{t.cap}</div>
                    <div className="sched-cap-cell-avail" style={{ color: 'rgba(255,255,255,0.55)' }}>{Math.max(0, t.cap - Math.round(t.sch))} avail</div>
                  </td>
                );
              })}
            </tr>

            {/* Department rows */}
            {tree.map(dept => {
              const isExpanded = expandedDepts[dept.id] ?? !!searchTerm; // collapsed unless searching
              return [
                <tr key={dept.id} className="sched-cap-dept-row" onClick={() => toggleDept(dept.id, isExpanded)} style={{ cursor: 'pointer' }}>
                  <td className="sched-cap-name-col" style={{ background: dept.color, color: '#fff', fontWeight: 700, fontSize: 12 }}>
                    <span style={{ marginRight: 6 }}>{isExpanded ? '▾' : '▸'}</span>
                    {dept.label}
                    <span style={{ opacity: 0.6, marginLeft: 6, fontWeight: 400, fontSize: 11 }}>
                      ({dept.levels.reduce((s, l) => s + l.people.length, 0)})
                    </span>
                  </td>
                  {weeks.map((w, wi) => {
                    const s = sumDept(dept, wi);
                    const pct = s.cap > 0 ? (s.sch / s.cap) * 100 : 0;
                    const c = utilColor(pct);
                    return (
                      <td key={wi} className="sched-cap-cell" style={{ background: c.bg }}
                        title={compact ? `${w.range}: ${Math.round(s.sch)}/${s.cap}h` : undefined}>
                        <div className="sched-cap-bar-track"><div style={{ height: 4, width: `${Math.min(pct, 100)}%`, background: c.bar, borderRadius: 2 }} /></div>
                        <div className="sched-cap-cell-main">{Math.round(s.sch)}/{s.cap}</div>
                        <div className="sched-cap-cell-avail" style={{ color: s.cap - s.sch > 0 ? c.text : '#EF4444' }}>
                          {s.cap - Math.round(s.sch) > 0 ? `${s.cap - Math.round(s.sch)} avail` : 'FULL'}
                        </div>
                      </td>
                    );
                  })}
                </tr>,

                // Level rows
                isExpanded && dept.levels.map(level => {
                  const levelExpanded = expandedLevels[level.id] ?? !!searchTerm;
                  return [
                    <tr key={level.id} className="sched-cap-level-row" onClick={() => toggleLevel(level.id, levelExpanded)} style={{ cursor: 'pointer' }}>
                      <td className="sched-cap-name-col sched-cap-level-label">
                        <span style={{ marginRight: 6 }}>{levelExpanded ? '▾' : '▸'}</span>
                        {level.name}
                        <span style={{ opacity: 0.5, marginLeft: 6, fontSize: 11 }}>({level.people.length})</span>
                      </td>
                      {weeks.map((w, wi) => {
                        const s = sumLevel(level, wi);
                        const pct = s.cap > 0 ? (s.sch / s.cap) * 100 : 0;
                        const c = utilColor(pct);
                        return (
                          <td key={wi} className="sched-cap-cell" style={{ background: c.bg }}
                            title={compact ? `${w.range}: ${Math.round(s.sch)}/${s.cap}h` : undefined}>
                            <div className="sched-cap-bar-track"><div style={{ height: 4, width: `${Math.min(pct, 100)}%`, background: c.bar, borderRadius: 2 }} /></div>
                            <div className="sched-cap-cell-main">{Math.round(s.sch)}/{s.cap}</div>
                            <div className="sched-cap-cell-avail" style={{ color: c.text }}>{Math.max(0, s.cap - Math.round(s.sch))} avail</div>
                          </td>
                        );
                      })}
                    </tr>,

                    // Person rows
                    levelExpanded && level.people.map(person => (
                      <tr key={person.StaffId} className="sched-cap-person-row">
                        <td className="sched-cap-name-col sched-cap-person-label">
                          <div className="sched-cap-person-avatar" style={{ background: dept.color }}>
                            {person.Initials || '??'}
                          </div>
                          <div>
                            <div className="sched-cap-person-name">
                              {person.Name}
                              <ContractChip contract={getContract(person.StaffId, startISO)} showFullTime style={{ marginLeft: 6 }} />
                            </div>
                            <div className="sched-cap-person-office">{officeOf(person)}</div>
                          </div>
                        </td>
                        {weeks.map((w, wi) => {
                          const pw = personWeek(person, wi);
                          const pct = pw.capacity > 0 ? (pw.scheduled / pw.capacity) * 100 : 0;
                          const c = utilColor(pct);
                          const avail = pw.capacity - Math.round(pw.scheduled);
                          return (
                            <td key={wi} className="sched-cap-cell sched-cap-cell-person" style={{ background: c.bg, cursor: 'pointer' }}
                              onMouseEnter={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setTooltip({
                                  x: r.left + r.width / 2,
                                  y: r.top,
                                  personName: person.Name,
                                  weekRange: w.range,
                                  jobs: pw.jobs,
                                  total: Math.round(pw.scheduled * 100) / 100,
                                });
                              }}
                              onMouseLeave={() => setTooltip(null)}
                            >
                              <div className="sched-cap-bar-track"><div style={{ height: 4, width: `${Math.min(pct, 100)}%`, background: c.bar, borderRadius: 2 }} /></div>
                              <div className="sched-cap-cell-main">{Math.round(pw.scheduled * 100) / 100}/{pw.capacity}</div>
                              <div className="sched-cap-cell-avail" style={{ color: avail > 0 ? c.text : '#EF4444', fontWeight: avail <= 0 ? 700 : 400 }}>
                                {avail > 0 ? `${avail} avail` : 'FULL'}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    )),
                  ];
                }),
              ];
            })}
          </tbody>
        </table>
        )}
        {!busy && tree.length === 0 && (
          <div className="sched-cap-empty">No staff match the current filters.</div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div className="sched-cap-tooltip" style={{ left: tooltip.x, top: tooltip.y - 12 }}>
          <div className="sched-cap-tooltip-header">
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{tooltip.personName}</div>
              <div style={{ fontSize: 11, color: '#6B7280' }}>{tooltip.weekRange}</div>
            </div>
            <div className="sched-cap-tooltip-total">{tooltip.total}h</div>
          </div>
          {tooltip.jobs.length === 0 ? (
            <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', paddingTop: 8 }}>No jobs scheduled</div>
          ) : (
            <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 8 }}>
              {tooltip.jobs.map((job, i) => (
                <div key={i} className="sched-cap-tooltip-job">
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1F2937' }}>{job.client}</div>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>{job.desc}</div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{job.hours}h</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
